import { RootState } from "../../redux/configureStore";
import { createSelector } from "@reduxjs/toolkit";
import { MINIMUM_VOLUME_THRESHOLD } from "./constants";
import sessionSlice, {
  SessionSetting,
  initialSessionState,
  sessionEntities,
} from "./sessionSlice";
import {
  StreamId,
  VenueArchiveLayout,
  VenueConnectionData,
  VenueSessionLayout,
} from "../../app/types";

const globalSelectors = sessionEntities.getSelectors(
  (state: RootState) => state[sessionSlice.name],
);

const getVenueSessionReduxState =
  (sessionId: number | undefined) => (state: RootState) => {
    if (sessionId) {
      return globalSelectors.selectById(state, sessionId);
    }
    return undefined;
  };

export function getVolume(
  sessionId: number | undefined,
  streamId: string,
): (state: RootState) => number {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => sessionState?.volume[streamId]?.average ?? 0,
  );
}

export function getTalker(
  sessionId: number | undefined,
): (state: RootState) => string | null {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => {
      const talker = Object.values(sessionState?.volume ?? {}).reduce(
        (prev, current) => {
          if (current?.average && current.average > (prev?.average ?? 0)) {
            return current;
          }
          return prev;
        },
        { average: 0, samples: [], streamId: "" },
      );

      if (talker === undefined || talker.average < MINIMUM_VOLUME_THRESHOLD) {
        return null;
      }
      return talker.streamId;
    },
  );
}

export function getPresenters(
  sessionId: number | undefined,
): (state: RootState) => VenueConnectionData[] {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => {
      const presenters = Object.values(
        sessionState?.presenters ?? {},
      ) as VenueConnectionData[];

      return presenters.filter(
        (value, index, self) =>
          index === self.findIndex((item) => item.id === value.id),
      );
    },
  );
}

export function getSubscribers(
  sessionId: number | undefined,
): (state: RootState) => VenueConnectionData[] {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => sessionState?.subscribers ?? [],
  );
}

export function getLayout(
  sessionId: number | undefined,
): (state: RootState) => VenueSessionLayout {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) =>
      sessionState?.layout ?? initialSessionState(sessionId ?? -1).layout,
  );
}

export function getComposedLayout(
  sessionId: number | undefined,
): (state: RootState) => VenueArchiveLayout {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) =>
      sessionState?.composedLayout ??
      initialSessionState(sessionId ?? -1).composedLayout,
  );
}

export function getMyScreenStreamId(
  sessionId: number | undefined,
): (state: RootState) => StreamId | null {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => sessionState?.myStreams.screen ?? null,
  );
}

export function getIsPublishingScreen(
  sessionId: number | undefined,
): (state: RootState) => boolean {
  return createSelector(
    getMyScreenStreamId(sessionId),
    (streamId) => streamId !== null,
  );
}

export function getMyCamStreamId(
  sessionId: number | undefined,
): (state: RootState) => StreamId | null {
  return createSelector(
    getVenueSessionReduxState(sessionId),
    (sessionState) => sessionState?.myStreams.camera ?? null,
  );
}

export function getIsPublishingCamera(
  sessionId: number | undefined,
): (state: RootState) => boolean {
  return createSelector(
    getMyCamStreamId(sessionId),
    (streamId) => streamId !== null,
  );
}

export const getIsPublishing = createSelector(
  getIsPublishingScreen,
  getIsPublishingCamera,
  (isPubScreen, isPubCam) => isPubCam || isPubScreen,
);

export const getSessionSetting = (
  sessionId: number | undefined,
  setting: SessionSetting,
) =>
  createSelector(
    getVenueSessionReduxState(sessionId),
    (session) => session?.settings[setting],
  );
